import { useList, useCustomMutation } from "@refinedev/core";
import { useState, useMemo, useEffect } from "react";
import { FormHeader } from "@/components/forms";
import { TableToolbar } from "@/components/tables";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuCheckboxItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Bot,
  CheckCircle,
  XCircle,
  Plus,
  Loader2,
  RefreshCw,
  Languages,
} from "lucide-react";
import { notify } from "@/lib/notify";

interface TranslationKey {
  key: string;
  namespace: string;
  translations: Record<string, string>;
  updatedAt: string;
}

interface EditingState {
  key: string;
  translations: Record<string, string>;
}

interface Language {
  code: string;
  name: string;
  nativeName: string;
  flagEmoji: string;
  isDefault: boolean;
}

const STORAGE_KEY = "bot-messages-visible-languages";

/**
 * BotMessagesList
 *
 * Компонент bot-messages для модуля translations
 *
 * @returns {React.ReactElement} React компонент
 */
export function BotMessagesList() {
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCategory, setSelectedCategory] = useState<string>("all");
  const [editingKey, setEditingKey] = useState<EditingState | null>(null);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [newKey, setNewKey] = useState({ key: "", translations: {} as Record<string, string> });
  const [visibleLanguages, setVisibleLanguages] = useState<string[]>([]);

  // Получаем список всех языков (как на странице /languages)
  const { result: languagesResult } = useList<Language>({
    resource: "languages",
    pagination: { pageSize: 100 },
  });

  const allLanguages = useMemo(() => {
    return (languagesResult?.data ?? []) as Language[];
  }, [languagesResult]);

  // Загружаем сохранённые языки при первом рендере
  useEffect(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        if (Array.isArray(parsed) && parsed.length > 0) {
          setVisibleLanguages(parsed);
          return;
        }
      } catch {
        // ignore
      }
    }
    // По умолчанию показываем все языки
    if (allLanguages.length > 0 && visibleLanguages.length === 0) {
      setVisibleLanguages(allLanguages.map(l => l.code));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allLanguages]);

  // Сохраняем выбор в localStorage
  const handleLanguageToggle = (code: string) => {
    setVisibleLanguages(prev => {
      const newValue = prev.includes(code)
        ? prev.filter(c => c !== code)
        : [...prev, code];
      localStorage.setItem(STORAGE_KEY, JSON.stringify(newValue));
      return newValue;
    });
  };

  const handleSelectAllLanguages = () => {
    const allCodes = allLanguages.map(l => l.code);
    setVisibleLanguages(allCodes);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(allCodes));
  };

  const handleDeselectAllLanguages = () => {
    setVisibleLanguages([]);
    localStorage.setItem(STORAGE_KEY, JSON.stringify([]));
  };

  const { result, query } = useList<TranslationKey>({
    resource: "translation-keys/bot",
    pagination: { mode: 'off' },
  });

  const { mutate: updateTranslation, mutation: updateMutation } = useCustomMutation();
  const { mutate: createKey, mutation: createMutation } = useCustomMutation();

  const translations = result?.data ?? [];
  const total = result?.total ?? 0;
  const isLoading = query.isLoading;
  const refetch = query.refetch;
  const isUpdating = updateMutation?.isPending ?? false;
  const isCreating = createMutation?.isPending ?? false;

  // Языки для отображения в таблице
  const displayLanguages = useMemo(() => {
    if (visibleLanguages.length === 0) {return allLanguages;}
    return allLanguages.filter(l => visibleLanguages.includes(l.code));
  }, [allLanguages, visibleLanguages]);

  // Получение неймспейсов
  const namespaces = [...new Set(translations.map((t) => t.namespace))].sort();

  // Фильтрация по поиску и неймспейсу
  const filteredTranslations = translations.filter((item) => {
    const matchesSearch =
      !searchQuery || item.key.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesNamespace =
      selectedCategory === "all" || item.namespace === selectedCategory;
    return matchesSearch && matchesNamespace;
  });

  // Группировка по неймспейсам
  const groupedByNamespace = filteredTranslations.reduce((acc, item) => {
    const ns = item.namespace;
    if (!acc[ns]) {acc[ns] = [];}
    acc[ns].push(item);
    return acc;
  }, {} as Record<string, TranslationKey[]>);

  const handleEdit = (item: TranslationKey) => {
    const translationValues: Record<string, string> = {};
    // Используем все языки для редактирования
    for (const lang of allLanguages) {
      translationValues[lang.code] = item.translations?.[lang.code] ?? '';
    }
    setEditingKey({ key: item.key, translations: translationValues });
  };

  const handleSave = () => {
    if (!editingKey) {return;}

    updateTranslation(
      {
        url: `/translations/bot/${encodeURIComponent(editingKey.key)}`,
        method: "put",
        values: { translations: editingKey.translations },
      },
      {
        onSuccess: () => {
          notify.success("Перевод сохранён");
          setEditingKey(null);
          refetch();
        },
        onError: () => {
          notify.error("Ошибка сохранения");
        },
      }
    );
  };

  const handleCreate = () => {
    if (!newKey.key.startsWith("bot.")) {
      notify.error("Ключ должен начинаться с 'bot.'");
      return;
    }

    createKey(
      {
        url: "/translations/bot",
        method: "post",
        values: { key: newKey.key, translations: newKey.translations },
      },
      {
        onSuccess: () => {
          notify.success("Ключ создан");
          setAddDialogOpen(false);
          setNewKey({ key: "", translations: {} });
          refetch();
        },
        onError: () => {
          notify.error("Ошибка создания");
        },
      }
    );
  };

  return (
    <div className="space-y-6">
      <FormHeader
        title="Системные сообщения бота"
        description={`Управление переводами Telegram бота (${total} ключей)`}
        actions={
          <div className="flex gap-2">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline">
                  <Languages className="h-4 w-4 mr-2" />
                  Языки ({displayLanguages.length})
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <div className="flex gap-2 p-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    className="flex-1 text-xs"
                    onClick={handleSelectAllLanguages}
                  >
                    Все
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="flex-1 text-xs"
                    onClick={handleDeselectAllLanguages}
                  >
                    Очистить
                  </Button>
                </div>
                <DropdownMenuSeparator />
                {allLanguages.map((lang) => (
                  <DropdownMenuCheckboxItem
                    key={lang.code}
                    checked={visibleLanguages.length === 0 || visibleLanguages.includes(lang.code)}
                    onCheckedChange={() => handleLanguageToggle(lang.code)}
                    onSelect={(e) => e.preventDefault()}
                  >
                    <span className="mr-2">{lang.flagEmoji}</span>
                    {lang.name} ({lang.code.toUpperCase()})
                  </DropdownMenuCheckboxItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
            <Button variant="outline" onClick={() => refetch()}>
              <RefreshCw className="h-4 w-4 mr-2" />
              Обновить
            </Button>
            <Button onClick={() => setAddDialogOpen(true)}>
              <Plus className="h-4 w-4 mr-2" />
              Добавить ключ
            </Button>
          </div>
        }
        sticky
      />

      <div className="flex flex-wrap items-center gap-3">
        <TableToolbar
          searchValue={searchQuery}
          onSearchChange={setSearchQuery}
          searchPlaceholder="Поиск по ключу..."
          className="flex-1"
        />
        <Tabs
          value={selectedCategory}
          onValueChange={setSelectedCategory}
        >
          <TabsList className="flex-wrap h-auto">
            <TabsTrigger value="all">Все</TabsTrigger>
            {namespaces.slice(0, 6).map((ns) => (
              <TabsTrigger key={ns} value={ns} className="text-xs">
                {ns}
              </TabsTrigger>
            ))}
          </TabsList>
        </Tabs>
      </div>

      {isLoading ? (
        <Card>
          <CardContent className="py-8 text-center text-muted-foreground">
            <Loader2 className="h-6 w-6 animate-spin mx-auto mb-2" />
            Загрузка...
          </CardContent>
        </Card>
      ) : (total === 0 ? (
        <Card>
          <CardContent className="py-8">
            <div className="text-center space-y-4">
              <Bot className="h-12 w-12 mx-auto text-muted-foreground" />
              <div>
                <h3 className="font-medium">Ключи бота не найдены</h3>
                <p className="text-sm text-muted-foreground mt-1">
                  Нет ключей с префиксом <code>bot.*</code>
                </p>
              </div>
              <Button onClick={() => setAddDialogOpen(true)}>
                <Plus className="h-4 w-4 mr-2" />
                Добавить первый ключ
              </Button>
            </div>
          </CardContent>
        </Card>
      ) : (
        Object.entries(groupedByNamespace).map(([namespace, items]) => (
          <Card key={namespace}>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Bot className="h-5 w-5" />
                {namespace}
                <Badge variant="secondary">{items.length}</Badge>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left p-2 font-medium">Ключ</th>
                      {displayLanguages.map((lang) => (
                        <th key={lang.code} className="text-left p-2 font-medium min-w-[200px]">
                          <span className="mr-1">{lang.flagEmoji}</span>
                          {lang.code.toUpperCase()}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((item) => (
                      <tr
                        key={item.key}
                        className="border-b hover:bg-muted/50 cursor-pointer"
                        onClick={() => handleEdit(item)}
                      >
                        <td className="p-2">
                          <code className="text-xs font-mono bg-muted px-1.5 py-0.5 rounded">
                            {item.key}
                          </code>
                        </td>
                        {displayLanguages.map((lang) => {
                          const value = item.translations?.[lang.code];
                          const isTranslated = !!value;
                          return (
                            <td key={lang.code} className="p-2">
                              <div className="flex items-start gap-2">
                                {isTranslated ? (
                                  <CheckCircle className="h-4 w-4 text-green-500 shrink-0 mt-0.5" />
                                ) : (
                                  <XCircle className="h-4 w-4 text-muted-foreground shrink-0 mt-0.5" />
                                )}
                                <span
                                  className={`text-sm line-clamp-2 ${
                                    isTranslated ? "" : "text-muted-foreground italic"
                                  }`}
                                >
                                  {value || "Не переведено"}
                                </span>
                              </div>
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        ))
      ))}

      {/* Edit Dialog */}
      <Dialog open={!!editingKey} onOpenChange={() => setEditingKey(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Редактировать перевод</DialogTitle>
          </DialogHeader>
          {editingKey && (
            <div className="space-y-4">
              <div>
                <Label>Ключ</Label>
                <Input value={editingKey.key} disabled className="bg-muted font-mono text-sm" />
              </div>
              {allLanguages.map((lang) => (
                <div key={lang.code}>
                  <Label>
                    <span className="mr-2">{lang.flagEmoji}</span>
                    {lang.name} ({lang.code.toUpperCase()})
                  </Label>
                  <Textarea
                    value={editingKey.translations[lang.code] || ""}
                    onChange={(e) =>
                      setEditingKey((prev) =>
                        prev
                          ? {
                              ...prev,
                              translations: {
                                ...prev.translations,
                                [lang.code]: e.target.value,
                              },
                            }
                          : null
                      )
                    }
                    rows={3}
                    placeholder={`Перевод на ${lang.name}...`}
                  />
                </div>
              ))}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditingKey(null)}>
              Отмена
            </Button>
            <Button onClick={handleSave} disabled={isUpdating}>
              {isUpdating && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Сохранить
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Dialog */}
      <Dialog open={addDialogOpen} onOpenChange={setAddDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Добавить ключ</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Ключ (должен начинаться с bot.)</Label>
              <Input
                value={newKey.key}
                onChange={(e) => setNewKey((prev) => ({ ...prev, key: e.target.value }))}
                placeholder="bot.welcome.title"
                className="font-mono text-sm"
              />
            </div>
            {allLanguages.map((lang) => (
              <div key={lang.code}>
                <Label>
                  <span className="mr-2">{lang.flagEmoji}</span>
                  {lang.name} ({lang.code.toUpperCase()})
                </Label>
                <Textarea
                  value={newKey.translations[lang.code] || ""}
                  onChange={(e) =>
                    setNewKey((prev) => ({
                      ...prev,
                      translations: {
                        ...prev.translations,
                        [lang.code]: e.target.value,
                      },
                    }))
                  }
                  rows={2}
                  placeholder={`Перевод на ${lang.name}...`}
                />
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddDialogOpen(false)}>
              Отмена
            </Button>
            <Button onClick={handleCreate} disabled={isCreating || !newKey.key}>
              {isCreating && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Создать
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
